import React, { useEffect, useMemo, useState } from 'react';
import { Button, Calendar, Groupper, Header, Icon, IconName, Table, useFrontier } from 'react-frontier2';
import { useTitle } from '@arema/components/Hooks';
import { useTranslation } from 'react-i18next';
import { getNowLocal } from '@arema/components/Util';
import { DateTime } from 'luxon';
import API from '../../API';
import { Link } from 'react-router-dom';

enum DateMode{
	DATE = 'date',
	MONTH = 'month'
}

var Papeleta = ()=>{
	var { t } = useTranslation();
	var { Toast } = useFrontier();
	var [calendarDate, setCalendarDate] = useState<DateTime>(null);
	var [days, setDays] = useState<{ date: Date, deposits: number }[]>(null);
	var [loadError, setLoadError] = useState<string>(null);
	var [mode, setMode] = useState<DateMode>(DateMode.DATE);
	var { setTitle } = useTitle();

	useEffect(()=>{
		setTitle('Papeleta');
		API.getPapeletaDays().then(res=>{
			if(res.error) return setLoadError(res.message);
			var resdates : typeof days = [];
			for(var i of res.data){
				resdates.push({
					date: DateTime.fromJSDate(new Date(i.date)).toJSDate(),
					deposits: i.deposits,
				})	
			}
			setDays(resdates);
		}).catch(err=>{
			setLoadError(t('common.unexpected_code', { code: 'LCL-PPLGT-1' }));
		});
	}, []);

	var now = useMemo(()=>{
		return getNowLocal();
	}, []);

	var selected_date = useMemo(()=>{
		if(!calendarDate) return null;
		return calendarDate;
	}, [calendarDate]);

	var downloadZip = ()=>{
		if(!calendarDate) return;
		var tx = Toast.show('Descargando archivo', {
			loading: true,
			dismissable: false,
		});
		API.downloadPapeletaZip(calendarDate.toSeconds(), false).then(res=>{
			if(!res.error) return;
			Toast.error(res.message);
		}).catch(err=>{
			Toast.error(t('common.unexpected_code', { code: 'LCL-PPLDW-1' }));
		}).finally(()=>{
			Toast.dismiss(tx);
		})
	}

	var downloadPdvDeposits = ()=>{
		if(!calendarDate) return;
		var tx = Toast.show('Descargando archivo', {
			loading: true,
			dismissable: false,
		});
		API.downloadPdvDeposits(calendarDate.toSeconds()).then(res=>{
			if(!res.error) return;
			Toast.error(res.message);
		}).catch(err=>{
			Toast.error(t('common.unexpected_code', { code: 'LCL-PPLPVDW-1' }));
		}).finally(()=>{
			Toast.dismiss(tx);
		})
	}

	var changeMode = (m: DateMode)=>{
		return ()=>{
			setCalendarDate(null);
			setMode(m);
		}
	}

	var options : {
		text: string,
		subtext?: string,
		iconName?: IconName,
		divider?: boolean,
		href?: string,
		onClick?: ()=>void,
	}[] = useMemo(()=>{
		var opts : typeof options = [];
		if(!selected_date) return opts;
		if(mode===DateMode.DATE){
			opts = [
				{ text: 'Papeleta', iconName: 'clipboard', href: `/conciliacion/papeleta/${selected_date.toFormat('yyyy-MM-dd')}`, subtext: 'Información de depositos por evento y forma de pago' },
				{ text: 'Origenes', iconName: 'file-invoice-dollar', href: `/conciliacion/origenes/${selected_date.toFormat('yyyy-MM-dd')}`, subtext: 'Información de los origenes de los depositos' },
				{ text: 'Taquillas', iconName: 'cash-register', href: `/conciliacion/papeleta/${selected_date.toFormat('yyyy-MM-dd')}/pdv`, subtext: 'Información de depositos por taquilla' },
				{ text: 'Descargables', divider: true },
				{ text: 'Poliza CONTPAQ', iconName: 'file-text', onClick: downloadZip, subtext: "Archivo de conciliación de Contpaq de ventas" },
			]
		}else{
			opts = [
				{ text: 'Papeleta mensual', iconName: 'clipboard', href: `/conciliacion/papeleta/month/${selected_date.toFormat('MM-yyyy')}`, subtext: 'Información de depositos por evento y forma de pago de todo el mes' },
				{ text: 'Origenes mensuales', iconName: 'file-invoice-dollar', href: `/conciliacion/origenes/month/${selected_date.toFormat('MM-yyyy')}`, subtext: 'Información de los origenes de los depositos de todo el mes' },
			]
		}
		return opts;
	}, [selected_date, mode]);

	if(loadError) return <Header text={t('common.error')} subtext={loadError} />
	if(!days && !loadError) return <Header loading text='Cargando papeleta' />
	
	return <Groupper fitted 
		title='Papeleta' 
		style={{ maxWidth: 500, margin: 'auto' }}
		titleRight={(
			<Button size='tiny' iconRight='caret-down' text='Tipo' menu={[
				{ text: 'Diario', onClick: changeMode(DateMode.DATE), iconName: mode===DateMode.DATE ? 'check' : null },
				{ text: 'Mensual', onClick: changeMode(DateMode.MONTH), iconName: mode===DateMode.MONTH ? 'check' : null },
			]} />
		)}
	>
		<Calendar 
			style={{ borderRadius: 0 }}
			mode={mode}
			date={calendarDate ? calendarDate.toJSDate() : null}
			onSelected={d=>setCalendarDate(DateTime.fromJSDate(d))}
			initialDate={now.toJSDate()}
			activeDays={days.map(a=>a.date)}
		/>
		{!!selected_date && <>
			{/* <Groupper.Divider centered text={selected_date.toFormat('dd/MMM/yyyy')} /> */}
			<Table fitted>
				<Table.Head title={selected_date?.toFormat(mode===DateMode.DATE ? 'dd/MMM/yyyy' : 'MMM/yyyy')} titleCentered />
				<Table.Body>
					{options.map(a=>(
						a.divider ? (
							<Table.Row header>
								<Table.Cell colSpan={100} value={a.text} />
							</Table.Row>
						) : (
							<Table.Row selectable as={a.href ? Link : 'div'} to={a.href} compact={!!a.href} onClick={a.onClick}>
								<Table.Cell collapsing centered style={{ paddingRight: 0 }}>
									<Icon name={a.iconName} size={25} fitted style={{ lineHeight: '30px' }} />
								</Table.Cell>
								<Table.Cell>
									<div className="text" style={{ fontWeight: 'bold' }}>{a.text}</div>
									{!!a.subtext && (
										<div className="subtext" style={{ fontSize: 12, lineHeight: '12px', color: 'gray' }}>
											{a.subtext}
										</div>
									)}
								</Table.Cell>
							</Table.Row>
						)
					))}
				</Table.Body>
			</Table>
			{/* <Items
				selectable
				className='divided'
				data={[
					{ text: selected_date.toFormat(mode===DateMode.DATE ? 'dd/MMM/yyyy' : 'MMM/yyyy'), type: 'divider' },
					...(mode===DateMode.DATE ? [
						{ text: 'Papeleta', icon: 'clipboard', href: `/conciliacion/papeleta/${selected_date.toFormat('yyyy-MM-dd')}`, subtext: 'Información de depositos por evento y forma de pago' },
						{ text: 'Origenes', icon: 'file invoice dollar', href: `/conciliacion/origenes/${selected_date.toFormat('yyyy-MM-dd')}`, subtext: 'Información de los origenes de los depositos' },
						{ text: 'Taquillas', icon: 'cash register', href: `/conciliacion/papeleta/${selected_date.toFormat('yyyy-MM-dd')}/pdv`, subtext: 'Información de depositos por taquilla' },
						{ text: 'Descargables', type: 'divider' },
						{ text: 'Poliza CONTPAQ', icon: 'file text', onClick: downloadZip, subtext: "Archivo de conciliación de Contpaq de ventas" },
					] : [
						{ text: 'Papeleta mensual', icon: 'clipboard', href: `/conciliacion/papeleta/month/${selected_date.toFormat('MM-yyyy')}`, subtext: 'Información de depositos por evento y forma de pago de todo el mes' },
						{ text: 'Origenes mensuales', icon: 'file invoice dollar', href: `/conciliacion/origenes/month/${selected_date.toFormat('MM-yyyy')}`, subtext: 'Información de los origenes de los depositos de todo el mes' },
					])
					// { text: 'Poliza Taquillas', icon: 'file text', onClick: downloadPdvDeposits, subtext: 'Archivo de conciliación de depositos de taquillas' },
				]}
				style={{ borderBottomLeftRadius: 8, borderBottomRightRadius: 8, overflow: 'hidden' }}
				itemStyle={{ fontSize: 16, padding: 0 }}
				render={a=>{
					if(a.type==='divider'){
						return <div style={{ backgroundColor: '#F9FAFB', textAlign: 'center', width: '100%', cursor: 'default', padding: 10, fontWeight: 'bold', fontSize: 14 }}>
							{a.text}
						</div>
					}
					const Comp = a.onClick ? 'div' : 'a';
					return <Comp style={{ padding: 15, color: 'black', width: '100%', flexDirection: 'row', alignItems: 'center', display: 'flex' }} href={a.href || undefined} target={a.href ? '_blank' : undefined} onClick={a.onClick}>
						<i className={`${a.icon} icon`} style={{ width: 30, minWidth: 30, textAlign: 'center' }}></i>
						<div className="content" style={{  }}>
							<div className="text" style={{ fontWeight: 'bold' }}>{a.text}</div>
							{!!a.subtext && (
								<div className="subtext" style={{ fontSize: 12, lineHeight: '12px', color: 'gray' }}>
									{a.subtext}
								</div>
							)}
						</div>
					</Comp>
				}}
			/> */}
		</>}
	</Groupper>
}

export default Papeleta;